import React from 'react';
import { Colors, Text } from 'library';
import { Tooltip, styled } from '@mui/material';
import { tooltipClasses, type TooltipProps } from '@mui/material/Tooltip';

import { ErrorBox } from './ErrorBox';
import { BaseCredentials as OhioBaseCredentials } from '../components/ohio/BaseCredentials';
import { BaseCredentials as FloridaBaseCredentials } from '../components/florida/BaseCredentials';
import { BaseCredentials as IllinoisBaseCredentials } from '../components/illinois/BaseCredentials';
import { BaseCredentials as NorthCarolinaBaseCredentials } from '../components/northcarolina/BaseCredentials';
import { BaseCredentials as PennsylvaniaBaseCredentials } from '../components/pennsylvania/BaseCredentials';
import { useRequirementsAndPayContext } from '../context';

import { OHIO, FLORIDA, ILLINOIS, NORTH_CAROLINA, PENNSYLVANIA } from '../constants';

export function RequirementsHeader() {
    const { state } = useRequirementsAndPayContext();
    const baseCredentials = (() => {
        switch (state) {
            case OHIO:
                return <OhioBaseCredentials />;
            case FLORIDA:
                return <FloridaBaseCredentials />;
            case ILLINOIS:
                return <IllinoisBaseCredentials />;
            case NORTH_CAROLINA:
                return <NorthCarolinaBaseCredentials />;
            case PENNSYLVANIA:
                return <PennsylvaniaBaseCredentials />;
            default:
                return null;
        }
    })();

    if (!baseCredentials) {
        return (
            <>
                <Text variant="display" textStyle={{ paddingLeft: 20, paddingRight: 20 }}>
                    What are the <u>base requirements</u> for the position?
                </Text>
                <ErrorBox
                    text="Sorry, we are having a hard time detecting your state. Please go back and ensure an address/location is selected."
                    style={{ marginTop: 10 }}
                />
            </>
        );
    }

    const popoverContent = (
        <PopoverInner>
            <Text bold>All Tandem teachers have:</Text>
            {baseCredentials}
        </PopoverInner>
    );

    return (
        <Text variant="display" textStyle={{ paddingLeft: 20, paddingRight: 20 }}>
            What are the{' '}
            <LightTooltip title={popoverContent} placement="bottom-start" enterTouchDelay={0} leaveTouchDelay={5000}>
                <BaseRequirementsTrigger>base requirements</BaseRequirementsTrigger>
            </LightTooltip>{' '}
            for the position?
        </Text>
    );
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: Colors.secondarySoft,
        color: Colors.primary,
        boxShadow: theme.shadows[2],
        padding: 15,
        maxWidth: 360,
        borderRadius: 12,
    },
}));

const BaseRequirementsTrigger = styled('span')({
    textDecoration: 'underline',
    cursor: 'help',
});

const PopoverInner = styled('div')({
    padding: 5,
});
